import { Select, Table } from 'antd';
import { MouseEventHandler, useMemo, useState } from 'react';

import classes from './TicksSingleItemOutput.module.css';

import {
  DataQueryConfig,
  DataQueryFilter,
  DataQueryProductRow,
  DataQueryProductRowOutput,
  DataQueryProductServerInfo,
} from '../../../../types';
import withFilter from '../_shared/WithFilter';
import withSorter from '../_shared/WithSorter';
import { DEFAULT_PAGE_SIZE } from '../_shared/table-utils';
import PagingDataTable, { PaginationType } from './paging/PagingDataTable';

type TicksSingleItemOutputParams = {
  config?: DataQueryConfig;
  data?: DataQueryProductRow[];
  dataIndex?: string;
  dataLoading?: boolean;
  dataError?: string | object;
  products?: string[];
  product?: string;
  onSelectProduct?: (value: string, option: { value: string; label: string } | { value: string; label: string }[]) => void;
  productServerInfo?: DataQueryProductServerInfo;
  onDownload?: (selectedItem: string) => void;
  defaultPageSize?: number;
  onPageSizeChanged?: (previous: number, next: number) => void;
  currentPage?: number;
  onCursorBack?: MouseEventHandler<HTMLElement>;
  onCursorNext?: MouseEventHandler<HTMLElement>;
  onChangedFilters?: (filters: DataQueryFilter) => void;
};

/* eslint-disable react/jsx-key */

const keyOptions: { [key: string]: { label: string; messageTypes: string[] } } = {
  datetime: { label: 'Date/Time', messageTypes: ['all'] },
  micros: { label: 'Micros', messageTypes: ['all'] },
  sequencenumber: { label: 'Sequence Number', messageTypes: ['all'] },
  mtype: { label: 'Message Type', messageTypes: ['all'] },
  f: { label: 'Feed', messageTypes: ['all'] },
  product: { label: 'Product', messageTypes: ['all'] },
  receipttimestamp: { label: 'Receipt Timestamp', messageTypes: ['all'] },
  exchangetimestamp: { label: 'Exchange Timestamp', messageTypes: ['LVL'] },
  marketparticipant: { label: 'Market Participant', messageTypes: ['all'] },
  price: { label: 'Price', messageTypes: ['ADD', 'LVL', 'MOD', 'TRD'] },
  quantity: { label: 'Quantity', messageTypes: ['ADD', 'LVL', 'TRD'] },
  orderpriority: { label: 'Order Priority', messageTypes: ['TRD'] },
  submarketcenterparticipanttimestamp: { label: 'Sub Market Center Participant Timestamp', messageTypes: ['TRD'] },
  aggressorside: { label: 'Aggressor Side', messageTypes: ['TRD'] },
  tradevolume: { label: 'Trade Volume', messageTypes: ['TRD'] },
  tradecondition: { label: 'Trade Condition', messageTypes: ['TRD'] },
  salecondition: { label: 'Sale Condition', messageTypes: ['TRD'] },
  salescondition2: { label: 'Sales Condition 2', messageTypes: ['TRD'] },
  salecondition3: { label: 'Sale Condition 3', messageTypes: ['TRD'] },
  salecondition4: { label: 'Sale Condition 4', messageTypes: ['TRD'] },
  printable: { label: 'Printable', messageTypes: ['TRD'] },
  orderreferencenumber: { label: 'Order Reference Number', messageTypes: ['ADD', 'MOD', 'TRD'] },
  ordertype: { label: 'Order Type', messageTypes: ['ADD', 'MOD'] },
  leavesquantity: { label: 'Leaves Quantity', messageTypes: ['TRD'] },
  side: { label: 'Side', messageTypes: ['ADD', 'IMB', 'MOD', 'TRD'] },
  orderprice: { label: 'Order Price', messageTypes: ['TRD'] },
  orderquantity: { label: 'Order Quantity', messageTypes: ['TRD'] },
  exchangereceipttimestamp: { label: 'Exchange Receipt Timestamp', messageTypes: ['TRD'] },
  highlowlast: { label: 'High/Low/Last', messageTypes: ['TRD'] },
  numberoforders: { label: 'Number of Orders', messageTypes: ['LVL', 'TRD'] },
  isquote: { label: 'Is Quote', messageTypes: ['TRD'] },
  crosstype: { label: 'Cross Type', messageTypes: ['TRD'] },
  tradingmode: { label: 'Trading Mode', messageTypes: ['TRD'] },
  executionattribute: { label: 'Execution Attribute', messageTypes: ['TRD'] },
  bidmpid: { label: 'Bid MPID', messageTypes: ['TRD'] },
  askmpid: { label: 'Ask MPID', messageTypes: ['TRD'] },
  transactiontimestamp: { label: 'Transaction Timestamp', messageTypes: ['TRD'] },
  outsidepricelimits: { label: 'Outside Price Limits', messageTypes: ['TRD'] },
  tradingstatus: { label: 'Trading Status', messageTypes: ['TRD'] },
  mic: { label: 'MIC', messageTypes: ['QUO', 'STA', 'TRD'] },
  nmstimestamp: { label: 'NMS Timestamp', messageTypes: ['QUO', 'STA', 'TRD'] },
  level: { label: 'Level', messageTypes: ['LVL'] },
  quantitychangereason: { label: 'Quantity Change Reason', messageTypes: ['LVL'] },
  matchingsign: { label: 'Matching Sign', messageTypes: ['LVL'] },
  middleofthebook: { label: 'Middle of the Book', messageTypes: ['LVL'] },
  admindelete: { label: 'Admin Delete', messageTypes: ['LVL'] },
  totalimbalance: { label: 'Total Imbalance', messageTypes: ['IMB'] },
  marketimbalance: { label: 'Market Imbalance', messageTypes: ['IMB'] },
  farprice: { label: 'Far Price', messageTypes: ['IMB'] },
  nearprice: { label: 'Near Price', messageTypes: ['IMB'] },
  referenceprice: { label: 'Reference Price', messageTypes: ['IMB'] },
  clearingprice: { label: 'Clearing Price', messageTypes: ['IMB'] },
  closingonlyclearingprice: { label: 'Closing Only Clearing Price', messageTypes: ['IMB'] },
  ssrfillingprice: { label: 'SSR Filling Price', messageTypes: ['IMB'] },
  pairedquantity: { label: 'Paired Quantity', messageTypes: ['IMB'] },
  auctionquantity: { label: 'Auction Quantity', messageTypes: ['IMB'] },
  auctiontype: { label: 'Auction Type', messageTypes: ['IMB'] },
  pricevariationindicator: { label: 'Price Variation Indicator', messageTypes: ['IMB'] },
  primarylistingparticipant: { label: 'Primary Listing Participant', messageTypes: ['IMB', 'STA'] },
  collarupprice: { label: 'Collar Up Price', messageTypes: ['IMB'] },
  collardownprice: { label: 'Collar Down Price', messageTypes: ['IMB'] },
  crosscondition: { label: 'Cross Condition', messageTypes: ['IMB'] },
  compositemarketbidprice: { label: 'Composite Market Bid Price', messageTypes: ['IMB'] },
  compositemarketaskprice: { label: 'Composite Market Ask Price', messageTypes: ['IMB'] },
  indicativeprice: { label: 'Indicative Price', messageTypes: ['IMB'] },
  unpairedquantity: { label: 'Unpaired Quantity', messageTypes: ['IMB'] },
  unpairedside: { label: 'Unpaired Side', messageTypes: ['IMB'] },
  significantimbalance: { label: 'Significant Imbalance', messageTypes: ['IMB'] },
  bidquantity: { label: 'Bid Quantity', messageTypes: ['IMB', 'QUO'] },
  askquantity: { label: 'Ask Quantity', messageTypes: ['IMB', 'QUO'] },
  previousquantity: { label: 'Previous Quantity', messageTypes: ['MOD'] },
  previouspriority: { label: 'Previous Priority', messageTypes: ['MOD'] },
  customerorder: { label: 'Customer Order', messageTypes: ['MOD'] },
  changereason: { label: 'Change Reason', messageTypes: ['MOD'] },
  isoddlot: { label: 'Is Odd Lot', messageTypes: ['MOD'] },
  bidprice: { label: 'Bid Price', messageTypes: ['QUO'] },
  bestbidmic: { label: 'Best Bid MIC', messageTypes: ['QUO'] },
  askprice: { label: 'Ask Price', messageTypes: ['QUO'] },
  bestaskmic: { label: 'Best Ask MIC', messageTypes: ['QUO'] },
  quotecondition: { label: 'Quote Condition', messageTypes: ['QUO'] },
  limituplimitdownindicator: { label: 'Limit Up/Limit Down Indicator', messageTypes: ['QUO', 'STA'] },
  settlementcondition: { label: 'Settlement Condition', messageTypes: ['QUO'] },
  financialstatus: { label: 'Financial Status', messageTypes: ['QUO'] },
  marketcondition: { label: 'Market Condition', messageTypes: ['QUO'] },
  retailpriceimprovementindicator: { label: 'Retail Price Improvement Indicator', messageTypes: ['QUO'] },
  luldlowerlimit: { label: 'LULD Lower Limit', messageTypes: ['QUO', 'STA'] },
  luldupperlimit: { label: 'LULD Upper Limit', messageTypes: ['QUO', 'STA'] },
  nbbolimituplimitdownindicator: { label: 'NBBO Limit Up/Limit Down Indicator', messageTypes: ['QUO'] },
  finralimituplimitdownindicator: { label: 'FINRA Limit Up/Limit Down Indicator', messageTypes: ['QUO'] },
  shortsaleindicator: { label: 'Short Sale Indicator', messageTypes: ['QUO', 'STA'] },
  bidquoteid: { label: 'Bid Quote ID', messageTypes: ['QUO'] },
  askquoteid: { label: 'Ask Quote ID', messageTypes: ['QUO'] },
  primarylistingmic: { label: 'Primary Listing MIC', messageTypes: ['QUO'] },
  haltreason: { label: 'Halt Reason', messageTypes: ['STA'] },
  tradingevent: { label: 'Trading Event', messageTypes: ['STA'] },
  tradingeventtimestamp: { label: 'Trading Event Timestamp', messageTypes: ['STA'] },
  marketsession: { label: 'Market Session', messageTypes: ['STA'] },
  minimumorderquantity: { label: 'Minimum Order Quantity', messageTypes: ['STA'] },
  roundlotquantity: { label: 'Round Lot Quantity', messageTypes: ['STA'] },
  maxpricevariation: { label: 'Max Price Variation', messageTypes: ['STA'] },
  lastbidprice: { label: 'Last Bid Price', messageTypes: ['STA'] },
  lastbidquantity: { label: 'Last Bid Quantity', messageTypes: ['STA'] },
  lastaskprice: { label: 'Last Ask Price', messageTypes: ['STA'] },
  lastaskquantity: { label: 'Last Ask Quantity', messageTypes: ['STA'] },
  referenceid: { label: 'Reference ID', messageTypes: ['STA'] },
  tickid: { label: 'Tick ID', messageTypes: ['STA'] },
};

function makeTableColumns(messageType: string) {
  return Object.entries(keyOptions)
    .filter(([_key, opt]) => messageType === 'all' || opt.messageTypes.includes('all') || opt.messageTypes.includes(messageType))
    .map(([key, opt]) => <Table.Column title={opt.label} dataIndex={key} />);
}

const buildFilterTagLabel = (key: string, filter: DataQueryFilter) => {
  const columnLabel = keyOptions[key]?.label ?? '';
  const filterValue = filter.value ? filter.value : '[all]';
  return {
    label: `${columnLabel}: ${filterValue}`,
    value: key,
  };
};

export default function TicksSingleItemOutput({
  config = {
    products: [],
    feed: undefined,
    messages: undefined,
    filters: {},
  },
  data = undefined,
  dataLoading = false,
  dataError = undefined,
  products = [],
  product = undefined,
  onSelectProduct = () => {},
  productServerInfo = undefined,
  onDownload = () => {},
  defaultPageSize = DEFAULT_PAGE_SIZE,
  onPageSizeChanged = () => {},
  currentPage = 0,
  onCursorBack = () => {},
  onCursorNext = () => {},
  onChangedFilters = () => {},
}: TicksSingleItemOutputParams) {
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [filters, setFilters] = useState(config.filters ?? {});
  const selectedMessage = useMemo(() => (config?.messages?.length === 1 ? config?.messages[0] : null), [config?.messages]); // single message selected

  const mappedData = useMemo(() => {
    if (!data || !data.length) {
      return [];
    }

    const productData = data.find((details) => details.product === product);

    if (!productData) {
      return [];
    }

    return productData.output.map((row) =>
      Object.keys(row).reduce<{ [key: string]: unknown }>((acc, cur) => {
        acc[cur] = row[cur as keyof DataQueryProductRowOutput];
        return acc;
      }, {})
    );
  }, [product, data]);

  const handleFiltering = (filter: DataQueryFilter, property: number) => {
    const updatedFilters = { ...filters, [property]: filter };
    setFilters(updatedFilters);

    onChangedFilters(updatedFilters);
  };

  const handleClearFilter = (property: string) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { [property]: _, ...updatedFilters } = filters;
    setFilters(updatedFilters);

    onChangedFilters(updatedFilters);
  };

  const columns = (selectedMessage ? makeTableColumns(selectedMessage) : makeTableColumns('all')).map(({ props, type: Column }) => {
    const { props: enhancedProps, type: ColumnWithSort } = withSorter(Column)({ ...props });

    return withFilter(
      ColumnWithSort,
      filters,
      handleFiltering,
      handleClearFilter
    )({
      ...enhancedProps,
      key: props.dataIndex,
      hasCustomFilter: true,
      // extraFilters: used for the filter dropdown
      extraFilters: [],
    });
  });

  const handlePageSizeChanged = (previous: number, next: number) => {
    setPageSize(next);
    onPageSizeChanged(previous, next);
  };

  const filterKeys = Object.keys(filters);

  return (
    <>
      <div className={classes.activeFiltersSelection}>
        <div className={classes.labelContainer}>
          <label className="ant-typography" htmlFor="activeFilters">
            Active filters:
          </label>
        </div>
        <Select
          id="activeFilters"
          aria-label="Active filters"
          mode="tags"
          value={filterKeys}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          options={filterKeys.map((key) => buildFilterTagLabel(key, filters[key as keyof typeof filters] as any))}
          loading={dataLoading}
          disabled={dataLoading || !filterKeys.length}
          placeholder="None"
          onDeselect={handleClearFilter}
          className={classes.filterTags}
          popupClassName={classes.filterTagsDropdown}
        />
      </div>
      <PagingDataTable
        data={mappedData}
        tableColumns={columns}
        product={product}
        products={products}
        dataLoading={dataLoading}
        dataError={dataError}
        onSelectProduct={onSelectProduct}
        productServerInfo={productServerInfo}
        onDownload={onDownload}
        paginationType={PaginationType.CURSOR}
        currentPage={currentPage}
        onCursorBack={onCursorBack}
        onCursorNext={onCursorNext}
        pageSize={pageSize}
        onPageSizeChanged={handlePageSizeChanged}
      />
    </>
  );
}
